/**
 * The Rejections tab on the admin mobile-app page (SCH-2889).
 *
 * One row per recorded store rejection for the app, newest first, with the
 * reviewer's text, the remedy runs started for it and the store's latest word
 * on the version. The rejection itself lives on the operations-event spine,
 * joined by its `correlationId`. Nothing here has a table of its own: the
 * reviewer text is fetched on demand and cached on the rejected version's
 * `MobileAppStoreVersion.detail.rejectionTexts`, and a remedy run is derived
 * from its `app.rejection_remedy_started` / `app.rejection_remedy_ended` events
 * and the setup drive or prompt job they name.
 *
 * Every timestamp is ISO-8601. Every rejection is addressed by its
 * `correlationId`, never by an event public id.
 *
 * Plan: docs/plans/2026-10-07-0837-feat-mobile-app-rejections-tab-plan.md
 */

import { z } from 'zod'
import {
  StoreRejectionCauseClassSchema,
  StoreRejectionFetchStateSchema,
  StoreRejectionGuidelineCodeSchema,
  StoreRejectionPromptTargetSchema,
  StoreRejectionRemedyActionKindSchema,
  StoreRejectionRemedyRunStatusSchema,
  StoreRejectionStoreSchema,
  StoreRejectionTemplateIdSchema,
  StoreRejectionTextSourceSchema
} from '../../schemas/entities/store-rejection'
import { StoreVersionStateSchema } from '../../schemas/entities/mobile-deployment'
import { SetupGraphNodeIdSchema } from './setup-graph'

/** The longest reviewer text a paste may store. Apple's longest Resolution Center messages run a few thousand characters. */
export const STORE_REJECTION_TEXT_MAX_LENGTH = 20_000

// ==========================================
// Templates (KTD7)
// ==========================================

/**
 * What the tab shows about one remedy template. The catalog entry's prompt and
 * match rules stay in the backend.
 *
 * `resets` is set for a `graph` template: the setup-graph nodes it runs again,
 * not counting the store's resubmit node every remedy ends with (KTD6).
 * `target` is set for a `prompt` template: the one field it may write (KTD8).
 */
export const StoreRejectionTemplateSummarySchema = z.object({
  id: StoreRejectionTemplateIdSchema,
  title: z.string(),
  store: StoreRejectionStoreSchema,
  causeClass: StoreRejectionCauseClassSchema,
  actionKind: StoreRejectionRemedyActionKindSchema,
  /** Whether the remedy builds the app again before resubmitting. */
  rebuild: z.boolean(),
  resets: z.array(SetupGraphNodeIdSchema).optional(),
  target: StoreRejectionPromptTargetSchema.optional()
})
export type StoreRejectionTemplateSummary = z.infer<typeof StoreRejectionTemplateSummarySchema>

/**
 * A template offered on one rejection (R9). Templates that match the
 * rejection's guideline codes or keywords come first and carry `suggested`;
 * `matchedOn` says which rule matched, or `null` for a template that only
 * applies to the store and state.
 */
export const StoreRejectionTemplateOptionSchema = z.object({
  template: StoreRejectionTemplateSummarySchema,
  suggested: z.boolean(),
  matchedOn: z.enum(['guideline', 'keyword']).nullable()
})
export type StoreRejectionTemplateOption = z.infer<typeof StoreRejectionTemplateOptionSchema>

// ==========================================
// History
// ==========================================

/**
 * The reviewer's text for one rejection, or why it is missing (R6, R7).
 * `text` and `source` are null until a text is captured; `fetchState` then
 * says why. `guidelineCodes` are parsed from the text, and empty without one.
 */
export const StoreRejectionDetailSummarySchema = z.object({
  text: z.string().nullable(),
  source: StoreRejectionTextSourceSchema.nullable(),
  fetchState: StoreRejectionFetchStateSchema,
  guidelineCodes: z.array(StoreRejectionGuidelineCodeSchema)
})
export type StoreRejectionDetailSummary = z.infer<typeof StoreRejectionDetailSummarySchema>

/**
 * One remedy run, derived from its events and the drive or job it names.
 * `runId` is the setup-drive dispatch id for a graph template, or the prompt
 * job's AI execution id for a prompt template. `stopReason` names the step
 * where a run stopped short of `resubmitted` (R14). `templateId` is a plain
 * string so a run whose template has since left the catalog still renders.
 */
export const StoreRejectionRemedyRunSchema = z.object({
  runId: z.string(),
  templateId: z.string(),
  /** Every rejection this run answers: all open rejections of the same app, store and version (R12). */
  coveredCorrelationIds: z.array(z.string()),
  status: StoreRejectionRemedyRunStatusSchema,
  stopReason: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable()
})
export type StoreRejectionRemedyRun = z.infer<typeof StoreRejectionRemedyRunSchema>

/**
 * The remedy recorded on the spine (`app.rejection_remedied`), by an operator
 * or by a resubmitted run. `templateId` is null for a hand-recorded remedy and
 * for every remedy recorded before this tab (R4).
 */
export const StoreRejectionRecordedRemedySchema = z.object({
  causeClass: StoreRejectionCauseClassSchema,
  remedy: z.string(),
  resubmitted: z.boolean(),
  templateId: z.string().nullable(),
  recordedAt: z.string()
})
export type StoreRejectionRecordedRemedy = z.infer<typeof StoreRejectionRecordedRemedySchema>

/**
 * Why Run is unavailable on a rejection (R13).
 *
 * - `drive_open`          a setup drive is already open for the app
 * - `run_in_flight`       a run for the same app and store has not finished
 * - `not_current_target`  the rejected version is not the app's current target
 * - `developer_rejected`  the submission was withdrawn by us, not rejected by the store
 * - `not_open`            the rejection is already remedied
 */
export const STORE_REJECTION_RUN_BLOCKED_REASONS = [
  'drive_open',
  'run_in_flight',
  'not_current_target',
  'developer_rejected',
  'not_open'
] as const
export const StoreRejectionRunBlockedReasonSchema = z.enum(STORE_REJECTION_RUN_BLOCKED_REASONS)
export type StoreRejectionRunBlockedReason = z.infer<typeof StoreRejectionRunBlockedReasonSchema>

/**
 * Whether Run is available, and the sentence shown when it is not.
 * `canMarkSuperseded` is true for an open rejection whose version is no longer
 * the current target, which offers "Mark superseded" in place of Run.
 */
export const StoreRejectionRunAvailabilitySchema = z.object({
  available: z.boolean(),
  blockedReason: StoreRejectionRunBlockedReasonSchema.nullable(),
  message: z.string().nullable(),
  canMarkSuperseded: z.boolean()
})
export type StoreRejectionRunAvailability = z.infer<typeof StoreRejectionRunAvailabilitySchema>

/** The store's latest observation of the rejected version, from `MobileAppStoreVersion`. */
export const StoreRejectionObservedStoreStateSchema = z.object({
  storeState: StoreVersionStateSchema,
  vendorState: z.string(),
  observedAt: z.string()
})
export type StoreRejectionObservedStoreState = z.infer<typeof StoreRejectionObservedStoreStateSchema>

/** One recorded rejection on the tab (R1). */
export const StoreRejectionHistoryRowSchema = z.object({
  correlationId: z.string(),
  /** Null when neither the rejection nor its filing recorded a storefront. */
  store: StoreRejectionStoreSchema.nullable(),
  /** The marketing version, when known. */
  storeVersion: z.string().nullable(),
  /** The store's id for the version (`MobileAppStoreVersion.storeVersionKey`), when known. */
  storeVersionKey: z.string().nullable(),
  /** When the STORE rejected it. */
  occurredAt: z.string(),
  /** The store's own rejection state, e.g. Apple `REJECTED`, `METADATA_REJECTED`, `DEVELOPER_REJECTED`. */
  state: z.string().nullable(),
  /** Not remedied, and not `DEVELOPER_REJECTED` (R3). */
  open: z.boolean(),
  detail: StoreRejectionDetailSummarySchema,
  remedyRuns: z.array(StoreRejectionRemedyRunSchema),
  remedy: StoreRejectionRecordedRemedySchema.nullable(),
  latestStoreState: StoreRejectionObservedStoreStateSchema.nullable(),
  /** Empty on a closed rejection. Suggested templates come first (R9). */
  suggestedTemplates: z.array(StoreRejectionTemplateOptionSchema),
  run: StoreRejectionRunAvailabilitySchema
})
export type StoreRejectionHistoryRow = z.infer<typeof StoreRejectionHistoryRowSchema>

export const GetStoreRejectionHistoryResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    rows: z.array(StoreRejectionHistoryRowSchema),
    /** The tab badge (R3): rows with `open` set. */
    openCount: z.number().int().nonnegative()
  })
})
export type GetStoreRejectionHistoryResponse = z.infer<typeof GetStoreRejectionHistoryResponseSchema>

// ==========================================
// Reviewer text: paste and fetch again (R6)
// ==========================================

/** The rejection comes from the path; the body carries only the text. */
export const PasteStoreRejectionTextRequestSchema = z.object({
  text: z.string().trim().min(1, { message: 'Paste the reviewer message' }).max(STORE_REJECTION_TEXT_MAX_LENGTH)
})
export type PasteStoreRejectionTextRequest = z.infer<typeof PasteStoreRejectionTextRequestSchema>

export const PasteStoreRejectionTextResponseSchema = z.object({
  message: z.string(),
  data: StoreRejectionHistoryRowSchema
})
export type PasteStoreRejectionTextResponse = z.infer<typeof PasteStoreRejectionTextResponseSchema>

/**
 * `dispatched` is false when no fetch was started, for example because the
 * Apple web session is not usable; `fetchState` then says why.
 */
export const FetchStoreRejectionTextAgainResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    correlationId: z.string(),
    fetchState: StoreRejectionFetchStateSchema,
    dispatched: z.boolean()
  })
})
export type FetchStoreRejectionTextAgainResponse = z.infer<typeof FetchStoreRejectionTextAgainResponseSchema>

// ==========================================
// Remedy runs (R10–R15)
// ==========================================

/**
 * Run a template against a rejection. The run covers every open rejection of
 * the same app, store and version (R12); `correlationId` names the one clicked.
 */
export const RunStoreRejectionRemedyRequestSchema = z.object({
  correlationId: z.string().min(1).max(64),
  templateId: StoreRejectionTemplateIdSchema
})
export type RunStoreRejectionRemedyRequest = z.infer<typeof RunStoreRejectionRemedyRequestSchema>

export const RunStoreRejectionRemedyResponseSchema = z.object({
  message: z.string(),
  data: z.object({ run: StoreRejectionRemedyRunSchema })
})
export type RunStoreRejectionRemedyResponse = z.infer<typeof RunStoreRejectionRemedyResponseSchema>

/** Close a rejection whose version is no longer the app's current target (R13). */
export const MarkStoreRejectionSupersededRequestSchema = z.object({
  correlationId: z.string().min(1).max(64)
})
export type MarkStoreRejectionSupersededRequest = z.infer<typeof MarkStoreRejectionSupersededRequestSchema>

/** `recorded` is false when a remedy was already on record; the first one stands. */
export const MarkStoreRejectionSupersededResponseSchema = z.object({
  message: z.string(),
  data: z.object({ correlationId: z.string(), recorded: z.boolean() })
})
export type MarkStoreRejectionSupersededResponse = z.infer<typeof MarkStoreRejectionSupersededResponseSchema>

// ==========================================
// Remedies by guideline (R16, R17)
// ==========================================

/**
 * How a recorded remedy turned out (R17).
 *
 * - `in_progress`      a run that has not ended
 * - `resubmitted`      resubmitted, and the store has given no verdict yet
 * - `approved`         resubmitted, then the store approved the version
 * - `rejected_again`   resubmitted, then the store rejected the same version again
 * - `failed`           a run that stopped before resubmitting (R14)
 * - `no_change`        a prompt run that produced no change (R15)
 * - `superseded`       a run ended because its rejection was marked superseded
 * - `not_resubmitted`  a manual remedy recorded without a resubmission
 *
 * Play reports no review verdict, so a Play remedy never reads `approved`; it
 * reads `rejected_again` once a later Play rejection of the app arrives.
 */
export const STORE_REJECTION_REMEDY_OUTCOMES = [
  'in_progress',
  'resubmitted',
  'approved',
  'rejected_again',
  'failed',
  'no_change',
  'superseded',
  'not_resubmitted'
] as const
export const StoreRejectionRemedyOutcomeSchema = z.enum(STORE_REJECTION_REMEDY_OUTCOMES)
export type StoreRejectionRemedyOutcome = z.infer<typeof StoreRejectionRemedyOutcomeSchema>

/**
 * One recorded remedy on this app: a remedy run, or a remedy recorded by hand
 * from the Attention page (`kind: 'manual'`, with its note). A manual remedy
 * has no `runId` and no template; a run has no note.
 */
export const StoreRejectionLedgerRemedySchema = z.object({
  kind: z.enum(['run', 'manual']),
  runId: z.string().nullable(),
  /** A plain string, so a run whose template has since left the catalog still renders. */
  templateId: z.string().nullable(),
  /** The template's title, or null for a manual remedy or a template no longer in the catalog. */
  templateTitle: z.string().nullable(),
  /** The manual remedy's note. */
  note: z.string().nullable(),
  store: StoreRejectionStoreSchema.nullable(),
  /** The marketing version of the rejected App Store version, when known. */
  storeVersion: z.string().nullable(),
  /** The rejections the remedy answered. */
  coveredCorrelationIds: z.array(z.string()),
  /** When the run started, or when the manual remedy was recorded. */
  recordedAt: z.string(),
  /** When the run ended; null for an open run and for a manual remedy. */
  finishedAt: z.string().nullable(),
  outcome: StoreRejectionRemedyOutcomeSchema,
  /** Where a failed run stopped (R14). */
  stopReason: z.string().nullable()
})
export type StoreRejectionLedgerRemedy = z.infer<typeof StoreRejectionLedgerRemedySchema>

/** Remedy runs of one template across every app, by outcome. Manual remedies are not counted. */
export const StoreRejectionTemplateOutcomeCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  in_progress: z.number().int().nonnegative(),
  resubmitted: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
  rejected_again: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  no_change: z.number().int().nonnegative(),
  superseded: z.number().int().nonnegative()
})
export type StoreRejectionTemplateOutcomeCounts = z.infer<typeof StoreRejectionTemplateOutcomeCountsSchema>

/**
 * A template listed under a guideline, with its fleet-wide outcome counts and
 * the correlation ids of this app's open rejections it is suggested for.
 */
export const StoreRejectionGuidelineTemplateSchema = z.object({
  template: StoreRejectionTemplateSummarySchema,
  fleetOutcomes: StoreRejectionTemplateOutcomeCountsSchema,
  suggestedFor: z.array(z.string())
})
export type StoreRejectionGuidelineTemplate = z.infer<typeof StoreRejectionGuidelineTemplateSchema>

/**
 * One guideline row (R16). `code` is null for the "No guideline" row, which
 * holds remedies of rejections citing no guideline and the templates meant
 * for them. A remedy covering rejections that cite two guidelines is listed
 * under both.
 */
export const StoreRejectionGuidelineGroupSchema = z.object({
  code: StoreRejectionGuidelineCodeSchema.nullable(),
  /** A short title for the code or its nearest titled parent; null when none is known. */
  title: z.string().nullable(),
  /** Whether the remedy catalog matches on this exact code. */
  inCatalog: z.boolean(),
  templates: z.array(StoreRejectionGuidelineTemplateSchema),
  /** This app's recorded remedies for rejections citing the guideline, newest first. */
  remedies: z.array(StoreRejectionLedgerRemedySchema)
})
export type StoreRejectionGuidelineGroup = z.infer<typeof StoreRejectionGuidelineGroupSchema>

export const GetStoreRejectionRemediesResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    app: z.object({ id: z.string(), name: z.string() }),
    /** Guidelines in code order; the "No guideline" row comes last. */
    guidelines: z.array(StoreRejectionGuidelineGroupSchema),
    /** The fleet scan behind `fleetOutcomes` hit its ceiling, so those counts are floors. */
    fleetTruncated: z.boolean()
  })
})
export type GetStoreRejectionRemediesResponse = z.infer<typeof GetStoreRejectionRemediesResponseSchema>
